<template>
  <WdsStack align-start justify-space-between row>
    <slot name="title"></slot>
    <WdsButton
      v-if="canCreate"
      :disabled="fieldsLoading"
      primary
      class="tw-whitespace-nowrap"
      data-test="add-custom-field-button"
      @click="openCreate = true"
    >
      {{ $t('Add custom field') }}
    </WdsButton>
  </WdsStack>
  <div class="tw-flex tw-justify-center tw-min-h-[100px]">
    <div v-if="fieldsLoading" class="tw-text-center tw-pt-5 tw-pb-5">
      <WdsSpinner :indeterminate="fieldsLoading"></WdsSpinner>
    </div>
    <template v-else>
      <table v-if="fieldObjects.length > 0" class="table table-striped">
        <thead>
          <tr>
            <th class="tw-text-left">{{ $t('Field name') }}</th>
            <th class="tw-text-center">{{ $t('Required') }}</th>
            <th></th>
          </tr>
        </thead>
        <tbody>
          <tr
            v-for="fieldObject in fieldObjects as CustomField[]"
            :key="fieldObject.id!"
            class="tw-font-bold"
          >
            <td>{{ fieldObject.title }}</td>
            <td class="tw-text-center">
              {{ fieldObject.displayValidations() }}
            </td>
            <td class="tw-text-right">
              <template v-if="fieldObject.viewerCanUpdate">
                <template v-if="destroysInProgress.indexOf(fieldObject.id!) !== -1">
                  <WdsSpinner :indeterminate="true"></WdsSpinner>
                </template>
                <WdsMenu v-else z-index="21" placement="bottom-end">
                  <template #trigger>
                    <WdsButtonGhost :data-test="`custom-field-dropdown-${fieldObject.id}`">
                      <WdsIcon class="tw-cursor-pointer" name="overflow-menu-horizontal" />
                    </WdsButtonGhost>
                  </template>
                  <WdsMenuOption
                    :data-test="`custom-field-edit-${fieldObject.id}`"
                    @click="editCustomField(fieldObject)"
                  >
                    <WdsStack gap-sm align-center>
                      <WdsIcon :size="16" class="tw-cursor-pointer" name="edit" />
                      {{ $t('Edit') }}
                    </WdsStack>
                  </WdsMenuOption>
                  <WdsMenuOption
                    theme="DESTRUCTIVE"
                    :disabled="destroysInProgress.indexOf(fieldObject.id!) !== -1"
                    @click="performDeleteCustomFieldAction(fieldObject)"
                  >
                    <WdsStack gap-sm align-center>
                      <WdsIcon :size="16" class="tw-cursor-pointer" name="trash-can" />
                      {{ $t('Delete') }}
                    </WdsStack>
                  </WdsMenuOption>
                </WdsMenu>
              </template>
            </td>
          </tr>
        </tbody>
      </table>
      <BaseHtml
        v-else
        class="tw-text-center tw-pt-5 tw-pb-5"
        :html="
          canCreate
            ? $t('Click Add custom field above to start adding custom fields')
            : $t('No custom fields have been added')
        "
      ></BaseHtml>
    </template>
  </div>
  <AddCustomField
    v-if="canCreate"
    :show="openCreate"
    :applies-to="appliesTo"
    :account-id="accountId"
    :visibility="props.visibility"
    @cancel="cancelCreate"
    @success="successfulCreate"
  />

  <template v-if="fieldIdToEdit !== null">
    <EditCustomField
      :field-id="fieldIdToEdit"
      :applies-to="appliesTo"
      :show="true"
      :visibility="props.visibility"
      @cancel="cancelEdit"
      @success="successfulEdit"
    />
  </template>
  <template v-if="fieldToDelete">
    <ConfirmDeleteCustomField
      :show="true"
      :field="fieldToDelete"
      :confirm="confirmDeleteCustomField"
      :cancel="cancelDelete"
      :applies-to="appliesTo"
    />
  </template>
</template>

<script setup lang="ts">
import { computed, reactive, ref, Ref } from 'vue'
import {
  WdsButton,
  WdsButtonGhost,
  WdsIcon,
  WdsMenu,
  WdsMenuOption,
  WdsSpinner,
  WdsStack,
} from '@worksome/wds-components'
import { useMutation, useQuery } from '@vue/apollo-composable'
import { $t } from '@/composables/translation'
import AddCustomField from '@/components/Fields/AddCustomField.vue'
import ConfirmDeleteCustomField from '@/components/Fields/ConfirmDeleteCustomField.vue'
import EditCustomField from '@/components/Fields/EditCustomField.vue'
import {
  CustomFieldsListQuery,
  InheritedCustomFieldsListQuery,
} from '@/components/Fields/graphql/queries'
import { DeleteCustomFieldMutation } from '@/components/Fields/graphql/mutations'
import { newInstanceOfCustomField } from '@/components/Fields/utils/CustomFieldUtils'
import { CustomField } from '@/components/Fields/dataObjects/CustomField'
import BaseHtml from '@/components/BaseHtml.vue'
import { CustomFieldVisibility, TypeSupportingCustomFieldValues } from '@/gql/graphql'
import { useFragment } from '@/gql'
import { CustomFieldListFragment } from '@/components/Fields/graphql/fragments'

interface Props {
  accountId: string
  appliesTo: TypeSupportingCustomFieldValues
  visibility?: CustomFieldVisibility
  inheritedOnly: boolean
  canCreate: boolean
}

const props = withDefaults(defineProps<Props>(), {
  inheritedOnly: false,
  // eslint-disable-next-line vue/no-boolean-default
  canCreate: true,
  visibility: CustomFieldVisibility.Internal,
})
const openCreate: Ref<boolean> = ref(false)
const fieldIdToEdit: Ref<string | null> = ref(null)
const deletesNeedingConfirmation = reactive<string[]>([])
const destroysInProgress = reactive<string[]>([])

const query = computed(
  () => (props.inheritedOnly ? InheritedCustomFieldsListQuery : CustomFieldsListQuery) as any,
)

const {
  result: customFieldQueryResult,
  refetch,
  loading: fieldsLoading,
} = useQuery(
  query.value,
  {
    accounts: [props.accountId],
    appliesTo: [props.appliesTo],
  },
  {
    clientId: 'publicApiCustomFields',
  },
)

const { mutate: destroyCustomField } = useMutation(DeleteCustomFieldMutation, {
  update(cache, result) {
    const deleteCustomField = result.data?.deleteCustomField
    if (deleteCustomField) {
      cache.evict({ id: cache.identify(deleteCustomField) })
      destroysInProgress.splice(destroysInProgress.indexOf(deleteCustomField.id), 1)
    }
  },
  clientId: 'publicApiCustomFields',
})

const fieldObjects = computed<CustomField[]>(
  () =>
    customFieldQueryResult.value?.customFields.data.map((field) => {
      const fieldTyped = useFragment(CustomFieldListFragment, field)
      return newInstanceOfCustomField(fieldTyped, fieldTyped.fieldType)
    }) || [],
)

const fieldToDelete = computed<CustomField | null | undefined>(() => {
  if (deletesNeedingConfirmation.length === 0) {
    return null
  }
  const idToDelete = deletesNeedingConfirmation[0]

  return fieldObjects.value.find((field: CustomField) => field.id === idToDelete)
})

function successfulCreate() {
  refetch()
  openCreate.value = false
}

function cancelCreate() {
  openCreate.value = false
}

function cancelEdit() {
  fieldIdToEdit.value = null
}

function successfulEdit() {
  refetch()
  fieldIdToEdit.value = null
}

function editCustomField(field: CustomField) {
  fieldIdToEdit.value = field.id
}

function performDestroyCustomField(field: CustomField) {
  destroysInProgress.push(field.id as string)
  destroyCustomField({
    input: {
      customField: field.id!,
    },
  })
}

function performDeleteCustomFieldAction(field: CustomField) {
  if (field.isUsedInFieldValues) {
    deletesNeedingConfirmation.push(field.id as string)
  } else {
    performDestroyCustomField(field)
  }
}

function confirmDeleteCustomField(field: CustomField) {
  deletesNeedingConfirmation.splice(deletesNeedingConfirmation.indexOf(field.id as string), 1)
  performDestroyCustomField(field)
}

function cancelDelete(field: CustomField) {
  deletesNeedingConfirmation.splice(deletesNeedingConfirmation.indexOf(field.id as string), 1)
}
</script>

<style lang="scss" scoped>
tr.row-disabled {
  position: relative;

  &:after {
    position: absolute;
    inset: 0;
    background-color: #cccccc;
    opacity: 0.5;
  }
}

table .btn-group .btn .preloader.pls-blue {
  position: static;
  margin: 0;
  width: 1em;
}

table tr td {
  vertical-align: middle;
}
</style>
